import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../../services/api';
import KVKKModal from './KVKKModal';
import './Auth.css';

const RegisterPage: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [commercialConsent, setCommercialConsent] = useState(false);
  const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [devLink, setDevLink] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setDevLink(null);

    if (!kvkkConsent) {
      setError('Devam etmek için KVKK Aydınlatma Metnini onaylamanız gerekmektedir.');
      return;
    }

    if (!phone.trim()) {
      setError('Lütfen cep telefonu numaranızı giriniz. Mobil saha girişi ve hesap güvenliği için zorunludur.');
      return;
    }

    const cleanDigits = phone.replace(/\D/g, '');
    const normPhone = cleanDigits.startsWith('90') && cleanDigits.length === 12
      ? cleanDigits.slice(2)
      : (cleanDigits.startsWith('0') && cleanDigits.length === 11 ? cleanDigits.slice(1) : cleanDigits);

    if (normPhone.length !== 10 || !normPhone.startsWith('5')) {
      setError('Lütfen geçerli bir cep telefonu numarası giriniz (Örn: 05xx xxx xx xx).');
      return;
    }

    if (password.length < 8) {
      setError('Şifre en az 8 karakter uzunluğunda olmalıdır.');
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.register({
        fullName,
        email,
        phone: normPhone,
        password,
        kvkkConsent,
        commercialConsent
      });

      if (res.success) {
        setSuccessMsg(res.message);
        if (res.devVerificationLink) {
          setDevLink(res.devVerificationLink);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kayıt sırasında bir hata oluştu. Lütfen bilgilerinizi kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card wide-card">
        <div className="auth-header">
          <div className="auth-logo-badge">🛡️</div>
          <h2 className="auth-title">Yeni İSG Uzmanı Kaydı</h2>
          <p className="auth-subtitle">14 günlük ücretsiz deneme sürenizi başlatmak için kaydolun</p>
        </div>

        {error && <div className="auth-alert error">{error}</div>}
        
        {successMsg ? (
          <div className="auth-success-box">
            <div className="success-icon">📩</div>
            <h3>Doğrulama E-Postası Gönderildi!</h3>
            <p>{successMsg}</p>
            
            {devLink && (
              <div className="dev-mode-hint">
                <span className="dev-tag">Lokal Geliştirme Kolaylığı:</span>
                <p>E-posta gönderim simülasyonu devrede. Doğrudan linke tıklayabilirsiniz:</p>
                <a href={devLink} className="dev-verify-link">
                  👉 Hesabı Şimdi Doğrula
                </a>
              </div>
            )}

            <div className="auth-success-actions">
              <button 
                className="auth-btn-primary" 
                onClick={() => navigate('/login', { state: { registeredEmail: email } })}
              >
                Giriş Sayfasına Git
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-row">
              <div className="form-group">
                <label>Ad Soyad *</label>
                <input
                  type="text"
                  placeholder="Örn: Ahmet Yılmaz"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>

              <div className="form-group">
                <label>E-Posta Adresi *</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="username email"
                  placeholder="Örn: ahmet@uzmanisg.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cep Telefonu *</label>
                <input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  placeholder="Örn: 0555 123 45 67"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="auth-input"
                />
                <span style={{ fontSize: '0.78rem', color: '#666', marginTop: '3px' }}>
                  📱 Mobil PWA saha girişlerinizde kullanılacaktır.
                </span>
              </div>

              <div className="form-group">
                <label>Giriş Şifresi (En az 8 Karakter) *</label>
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>
            </div>

            {/* KVKK Onay Checkbox */}
            <div className="kvkk-checkbox-row">
              <input
                type="checkbox"
                id="kvkkCheck"
                checked={kvkkConsent}
                onChange={(e) => setKvkkConsent(e.target.checked)}
                className="custom-checkbox"
                required
              />
              <label htmlFor="kvkkCheck" className="kvkk-label">
                <button
                  type="button"
                  className="kvkk-link-btn"
                  onClick={() => setIsKvkkModalOpen(true)}
                >
                  KVKK Aydınlatma Metni
                </button>
                'ni okudum, kişisel verilerimin işlenmesini onaylıyorum. *
              </label>
            </div>

            {/* Ticari Elektronik İleti Onayı (ETK / İYS Uyumlu - İsteğe Bağlı) */}
            <div className="kvkk-checkbox-row" style={{ marginTop: '8px' }}>
              <input
                type="checkbox"
                id="commercialCheck"
                checked={commercialConsent}
                onChange={(e) => setCommercialConsent(e.target.checked)}
                className="custom-checkbox"
              />
              <label htmlFor="commercialCheck" className="kvkk-label" style={{ fontSize: '0.82rem', color: '#475569' }}>
                İSG mevzuat güncellemeleri, sistem duyuruları ve kampanyalardan e-posta/SMS yoluyla haberdar olmak istiyorum (İsteğe bağlı).
              </label>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Kaydınız Oluşturuluyor...' : 'Ücretsiz Kayıt Ol & 14 Gün Dene 🚀'}
            </button>
          </form>
        )}

        <div className="auth-footer-nav">
          Zaten bir hesabınız var mı? <Link to="/login">Giriş Yapın</Link>
        </div>
      </div>

      <KVKKModal
        isOpen={isKvkkModalOpen}
        onClose={() => setIsKvkkModalOpen(false)}
      />
    </div>
  );
};

export default RegisterPage;
